import type { QueryOptions, ApolloClient } from 'apollo-client';
import type { GraphQLTaggedNode } from 'relay-runtime';
import type { GraphQLError } from 'graphql';

import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { isExpectedApolloError } from '@confluence/error-types/entry-points/expectedGraphQlErrorHelper.ts';
import { toDocumentNode } from '@confluence/apollo-relay-interop/entry-points/toDocumentNode.ts';
import {
	writeQueryInterop,
	type WriteQueryInteropArgs,
} from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { fg } from '@confluence/feature-gating';

import { getOperationName, UNEXPECTED_RESPONSE } from './getOperationName.ts';
import { getQueryName } from './getQueryName.ts';
import { isUnauthorizedError } from './errors/isUnauthorizedError.ts';
import { isNotFoundError } from './errors/isNotFoundError.ts';
import { isNotFoundSpaceError } from './errors/isNotFoundSpaceError.ts';
import { trackPreloadedQuery } from './developmentQueryBoundaryVerification.ts';
import { getAtlAttributionHeader } from './getAtlAttributionHeader.ts';

const muteExpectedGraphQLErrors = (result: any) => {
	// Preloading is "fire and forget" operation on a client as well, so we can mute
	// some known errors, but we can't really do anything about them as they're not
	// make it to the Apollo cache anyway and therefore don't pop at where they can
	// be handled.
	const hasErrors = result?.errors?.length > 0;
	result?.errors?.forEach((error: any) => {
		if (
			isUnauthorizedError(error) ||
			isExpectedApolloError(error) ||
			isNotFoundSpaceError(error) ||
			isNotFoundError(error)
		) {
			markErrorAsHandled(error);
		}
	});

	return hasErrors;
};

/**
 * Same as Apollo's query function but only used in preloader functions
 */
export function query<Q, V>({
	query: _query,
	variables,
	/**
	 * How to preload with fresh data correctly.
	 * In preloader: query with fetchPolicy: "network-only"
	 */
	fetchPolicy = 'cache-first',
	errorPolicy = 'all',
	context,
	client = window.GLOBAL_APOLLO_CLIENT,
}: Omit<QueryOptions<V>, 'query'> & {
	// TODO: CCE2EPERF-655 update QueryOptions to queryInterop param type
	query: GraphQLTaggedNode;
	client?: ApolloClient<any>;
}): Promise<{
	data: Q | null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> {
	if (!client) {
		// Referencing to the client via global variable to avoid circular dependency
		// This should never happen and will be removed in the future
		return Promise.resolve(UNEXPECTED_RESPONSE);
	}

	const query = toDocumentNode(_query);
	const name = getOperationName(query);

	if (
		process.env.CLOUD_ENV === 'dev' ||
		process.env.CLOUD_ENV === 'branch' ||
		process.env.CLOUD_ENV === 'staging'
	) {
		if (window.__PRELOAD_BOUNDARY_NAME__) {
			trackPreloadedQuery(query, variables, window.__PRELOAD_BOUNDARY_NAME__);
		}
	}

	// This is indirect reference to next/packages/graphql/src/links/DevOnlyGraphqlErrorGenerator.ts
	if (process.env.NODE_ENV !== 'production' && (window as any).graphqlOperationController) {
		const operationController = (window as any).graphqlOperationController;
		const { shouldGenerateError } = operationController.onOperationRequested(name);
		if (shouldGenerateError) {
			return Promise.resolve(UNEXPECTED_RESPONSE);
		}
	}

	let single = true;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const queryName = getQueryName(name, variables);
		const { batchQueries } = getSSRRenderInputs();
		if (queryName in (batchQueries || {})) {
			single = false;
		}
	}

	const queryContext = {
		single,
		...context,
	};

	// Only add atl-attribution header if AGG migration feature gate is enabled
	if (fg('cc-agg-migration-use-agg-endpoint')) {
		queryContext.headers = {
			...(context?.headers || {}),
			...getAtlAttributionHeader(),
		};
	}

	if (window.__SSR_REACT_STREAMING__) {
		queryContext.preload = true;
		queryContext.originalQuery = query;
	}

	// Save current boundary context
	const currentBoundary = window.__PRELOAD_BOUNDARY_NAME__;

	// TODO: CCE2EPERF-926 needs to be updated to use query interop once we are on a single Apollo client
	return client
		.query({
			query,
			variables,
			errorPolicy,
			fetchPolicy,
			context: queryContext,
		})
		.then((result) => {
			// Restore the previous boundary context as soon as the query resolves
			window.__PRELOAD_BOUNDARY_NAME__ = currentBoundary;
			const hasErrors = muteExpectedGraphQLErrors(result);
			return { ...result, hasErrors };
		});
}

export function queryAgg<T, V>(
	options: Omit<QueryOptions<V>, 'query'> & {
		// TODO: CCE2EPERF-655 update QueryOptions to queryInterop param type
		query: GraphQLTaggedNode;
	},
) {
	return query<T, V>({
		...options,
		query: options.query,
		client: window.GLOBAL_APOLLO_AGG_CLIENT,
	});
}

/**
 * @deprecated use writeQueryInterop from `@confluence/apollo-relay-interop/entry-points/writeQuery`
 */
export function writeQuery<T, V extends Record<string, any>>(
	options: WriteQueryInteropArgs<T, V> & { client?: ApolloClient<any> },
) {
	if (process.env.BUILD_VARIANT === 'relay') {
		writeQueryInterop<T, V>(options);
	} else {
		const client = options.client || window.GLOBAL_APOLLO_CLIENT;
		if (client) {
			client.writeQuery({
				...options,
				query: toDocumentNode(options.query),
			});
		}
	}
}

/**
 * @deprecated use writeQueryInterop from `@confluence/apollo-relay-interop/entry-points/writeQuery`
 */
export function writeAggQuery<T, V extends Record<string, any>>(
	options: WriteQueryInteropArgs<T, V>,
) {
	writeQuery({ ...options, client: window.GLOBAL_APOLLO_AGG_CLIENT });
}
